import type { TableCellProps, TableProps, TableRowProps, ToolbarProps } from "@mui/material"

import { getTheme } from "../../hooks/UseTheme"

const theme = getTheme()

// Style configurations
export const getTableProps = (
  newStyling: boolean | undefined,
  disableToolbar: boolean | undefined,
  hasNoData?: boolean,
): TableProps => {
  if (newStyling) {
    return {
      sx: {
        borderRadius: "0px",
        padding: "0px",
        overflow: "hidden",
        height: hasNoData ? "100%" : "auto",
      },
    }
  } else {
    return {
      // TODO:: fix chipping at the edges
      sx: {
        border: disableToolbar ? "0px" : `1px solid ${theme.palette.base.border[1]}`,
        borderTop: "0px",
        borderBottom: "0px",
        borderRadius: "0px",
        padding: "0px 12px",
      },
    }
  }
}

export const getTopToolbarProps = (newStyling: boolean | undefined): ToolbarProps => {
  if (newStyling) {
    return {
      variant: "dense" as const,
      sx: {
        backgroundColor: theme.palette.base.background[1],
        borderRadius: "8px 8px 0px 0px",
        border: "none",
        borderBottom: `1px solid ${theme.palette.base.border[1]}`,
        zIndex: 0,
        padding: "0.25rem !important",
        "& > .MuiCollapse-root": {
          order: 1,
        },
      },
    }
  } else {
    return {
      variant: "dense" as const,
      sx: {
        backgroundColor: theme.palette.base.background[3],
        borderRadius: "8px 8px 0px 0px",
        border: `1px solid ${theme.palette.base.border[1]}`,
        borderBottom: "0px",
        zIndex: 0,
        padding: "0.25rem !important",
        "& > .MuiCollapse-root": {
          order: 1,
        },
      },
    }
  }
}

export const getBottomToolbarProps = (newStyling: boolean | undefined): ToolbarProps => {
  if (newStyling) {
    return {
      variant: "dense" as const,
      sx: {
        backgroundColor: theme.palette.base.background[1],
        borderRadius: "0px 0px 8px 8px",
        border: "none",
        borderBottom: `1px solid ${theme.palette.base.border[1]}`,
        marginTop: "auto",
        "> div.MuiBox-root > div": {
          justifyContent: "space-between",
          width: "100%",
        },
      },
    }
  } else {
    return {
      variant: "dense" as const,
      sx: {
        backgroundColor: theme.palette.base.background[1],
        borderRadius: "0px 0px 8px 8px",
        border: `1px solid ${theme.palette.base.border[1]}`,
        borderTop: "0px",

        // Splitting pagination section
        "> div.MuiBox-root > div": {
          justifyContent: "space-between",
          width: "100%",
        },
      },
    }
  }
}

export const getTableHeadCellProps = (
  newStyling: boolean | undefined,
  disableToolbar: boolean | undefined,
): TableCellProps => {
  if (newStyling) {
    return {
      sx: {
        borderBottom: `1px solid ${theme.palette.base.border[1]}`,
        borderRight: "none",
        backgroundColor: theme.palette.base.background[1],
        color: theme.palette.base.text[1],
        padding: "8px 12px !important",
        fontSize: "12px",
        fontWeight: 600,
        lineHeight: "16px",
        letterSpacing: "0%",
        textAlign: "left",
        height: "auto",
        opacity: 1,
        "> div.Mui-TableHeadCell-Content": {
          flexDirection: "row-reverse",
          justifyContent: "flex-end",
          width: "max-content",
          minHeight: "auto",
        },
      },
    }
  } else {
    return {
      sx: {
        borderBottom: `1px solid ${theme.palette.base.border[1]}`,
        backgroundColor: theme.palette.base.background[1],
        color: theme.palette.base.text[1],
        padding: "8px 8px 8px 4px !important",
        fontSize: "14px",
        fontWeight: 600,
        lineHeight: "20px",
        textAlign: "center",
        borderTop: disableToolbar ? `1px solid ${theme.palette.base.border[1]}` : "none",
        "> div.Mui-TableHeadCell-Content": {
          flexDirection: "row-reverse",
          justifyContent: "flex-end",
          marginBottom: "8px",
          width: "max-content",
        },
      },
    }
  }
}

export const getTableHeadRowProps = (
  newStyling: boolean | undefined,
  disableToolbar: boolean | undefined,
): TableRowProps => {
  if (newStyling) {
    return {
      sx: {
        boxShadow: "unset",
        "th:first-of-type": {
          borderTopLeftRadius: disableToolbar ? "8px" : "0px",
        },
        "th:last-of-type": {
          borderTopRightRadius: disableToolbar ? "8px" : "0px",
        },
      },
    }
  } else {
    return {
      sx: {
        boxShadow: "unset",
        "th:first-of-type": {
          paddingLeft: "12px !important",
        },
        "th:last-of-type": {
          paddingRight: "12px !important",
        },
      },
    }
  }
}

export const getTableBodyRowProps = (newStyling: boolean | undefined): TableRowProps => {
  if (newStyling) {
    return {
      sx: {
        "&:last-child td": { borderBottom: "none" },
        background: theme.palette.base.background[3],
      },
      hover: false,
    }
  } else {
    return {
      sx: {
        background: theme.palette.base.background[3],
        "td:first-of-type": {
          paddingLeft: "12px !important",
        },
        "td:last-of-type": {
          paddingRight: "12px !important",
        },
      },
      // TODO:: fix hover state colors
      hover: false,
    }
  }
}

export const getTableBodyCellProps = (newStyling: boolean | undefined): TableCellProps => {
  if (newStyling) {
    return {
      sx: {
        padding: "8px 12px",
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "20px",
        color: theme.palette.base.text[1],
        borderBottom: `1px solid ${theme.palette.base.border[1]}`,
        borderRight: "none",
      },
    }
  } else {
    return {
      sx: {
        padding: "8px",
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "20px",
        color: theme.palette.base.text[1],
        borderBottom: `1px solid ${theme.palette.base.border[1]}`,
      },
    }
  }
}
